import { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Divider, Stack, TextField, Typography } from '@mui/material';
import {
  CampaignOutlined, CardGiftcardOutlined, CelebrationOutlined, HandshakeOutlined, LinkOutlined,
  CheckCircleOutlined as CheckCircleOutlinedIcon, MovieCreationOutlined, WorkOutlineOutlined,
} from '@mui/icons-material';
import Button from './Button';
import { track } from '../lib/analytics';
import {
  BRAND_CATEGORY_OPTIONS, CREATOR_GOAL_OPTIONS, OPPORTUNITY_PREFERENCES,
  opportunityPreferenceLabel, opportunityProfileCompletion,
} from '../lib/opportunityPreferences';

const PROFILE_FIELDS = [
  'opportunityPreferences', 'collaborationWishlistBrands', 'collaborationWishlistCategories', 'creatorGoals',
];

const ICONS = {
  campaign: CampaignOutlined, video: MovieCreationOutlined, gift: CardGiftcardOutlined,
  event: CelebrationOutlined, link: LinkOutlined, handshake: HandshakeOutlined,
};

function profileValues(creator) {
  return Object.fromEntries(PROFILE_FIELDS.map((field) => [field, [...(creator[field] || [])]]));
}

function ToggleCards({ options, values, onChange, disabled = false }) {
  const knownKeys = new Set(options.map((option) => option.key));
  const displayOptions = [
    ...options,
    ...values.filter((value) => !knownKeys.has(value)).map((key) => ({ key, label: opportunityPreferenceLabel(key) })),
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
      {displayOptions.map((option) => {
        const selected = values.includes(option.key);
        const Icon = ICONS[option.icon] || WorkOutlineOutlined;
        return (
          <Box
            component="button"
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            key={option.key}
            onClick={() => onChange(selected ? values.filter((value) => value !== option.key) : [...values, option.key])}
            sx={{
              minHeight: 58, p: 1.5, borderRadius: 2, border: '1px solid', cursor: disabled ? 'default' : 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 1.25, font: 'inherit', color: disabled ? 'text.disabled' : selected ? 'primary.dark' : 'text.primary',
              borderColor: disabled ? 'divider' : selected ? 'primary.main' : 'divider', bgcolor: disabled ? 'action.hover' : selected ? 'rgba(78, 40, 181, 0.07)' : 'background.paper',
              '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: selected ? 'rgba(78, 40, 181, 0.1)' : 'action.hover' },
              '&:focus-visible': { outline: '3px solid rgba(78, 40, 181, 0.22)', outlineOffset: 2 },
            }}
          >
            <Icon sx={{ color: disabled ? 'text.disabled' : selected ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 650 }}>{option.label}</Typography>
            {selected && <CheckCircleOutlinedIcon sx={{ color: disabled ? 'text.secondary' : 'primary.main', ml: 'auto', flexShrink: 0 }} />}
          </Box>
        );
      })}
    </Box>
  );
}

function SelectableChips({ options, values, onChange, disabled = false }) {
  const displayOptions = options;
  return (
    <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
      {displayOptions.map((option) => {
        const selected = values.includes(option.key);
        return (
          <Chip
            key={option.key}
            label={option.label}
            clickable
            disabled={disabled}
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            onClick={() => onChange(selected ? values.filter((value) => value !== option.key) : [...values, option.key])}
            sx={{ height: 'auto', minHeight: 40, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }}
          />
        );
      })}
    </Stack>
  );
}

function OpportunityProfileEditor({ creator, onSave }) {
  const [draft, setDraft] = useState(() => profileValues(creator));
  const [editing, setEditing] = useState(false);
  const [brandInput, setBrandInput] = useState('');
  const [categoryOtherInput, setCategoryOtherInput] = useState('');
  const [goalOtherInput, setGoalOtherInput] = useState('');
  const [initialCompletionPercentage] = useState(() => opportunityProfileCompletion(creator).percentage);
  const completion = opportunityProfileCompletion(draft);
  const savedValues = useMemo(() => profileValues(creator), [creator]);

  useEffect(() => {
    setDraft(profileValues(creator));
    setEditing(false);
  }, [creator]);
  useEffect(() => {
    track('opportunity_profile_viewed', { label: `${initialCompletionPercentage}_percent_complete` });
    if (window.location.hash === '#opportunity-profile') {
      window.setTimeout(() => document.getElementById('opportunity-profile')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    }
  }, [initialCompletionPercentage]);

  function update(field, values) {
    setDraft((current) => ({ ...current, [field]: values }));
  }

  function addCustom(field, input, setInput) {
    const value = input.trim().replace(/\s+/g, ' ');
    if (!value || value.length > 100) return;
    if (draft[field].some((item) => item.toLowerCase() === `other:${value}`.toLowerCase())) return;
    const values = draft[field].filter((item) => item !== 'other');
    update(field, [...values, `other:${value}`]);
    setInput('');
  }

  function customValues(values) {
    return values.filter((value) => value.startsWith('other:')).map((value) => ({ key: value, label: value.slice(6) }));
  }

  function addBrand() {
    const brand = brandInput.trim().replace(/\s+/g, ' ');
    if (!brand || brand.length > 100 || draft.collaborationWishlistBrands.length >= 10) return;
    if (draft.collaborationWishlistBrands.some((value) => value.toLowerCase() === brand.toLowerCase())) {
      setBrandInput('');
      return;
    }
    update('collaborationWishlistBrands', [...draft.collaborationWishlistBrands, brand]);
    setBrandInput('');
  }

  function save() {
    if (JSON.stringify(draft.opportunityPreferences) !== JSON.stringify(savedValues.opportunityPreferences)) {
      track('opportunity_preferences_updated', { entityType: 'opportunity_preferences', label: draft.opportunityPreferences.join(',') || 'none_selected' });
    }
    if (JSON.stringify([draft.collaborationWishlistBrands, draft.collaborationWishlistCategories]) !== JSON.stringify([savedValues.collaborationWishlistBrands, savedValues.collaborationWishlistCategories])) {
      track('collaboration_wishlist_updated', { entityType: 'collaboration_wishlist', label: `${draft.collaborationWishlistBrands.length}_brands;categories:${draft.collaborationWishlistCategories.join(',') || 'none'}` });
    }
    if (JSON.stringify(draft.creatorGoals) !== JSON.stringify(savedValues.creatorGoals)) {
      track('creator_goals_updated', { entityType: 'creator_goals', label: draft.creatorGoals.join(',') || 'none_selected' });
    }
    if (completion.isComplete && !opportunityProfileCompletion(savedValues).isComplete) {
      track('opportunity_profile_completed', { label: '100_percent_complete' });
    }
    onSave(draft);
    setEditing(false);
  }

  return (
    <Card id="opportunity-profile" variant="outlined" sx={{ scrollMarginTop: 88 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="h6">Collaboration Preferences</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, maxWidth: 650 }}>
              Tell us what you're looking for so we can better understand the opportunities that are relevant to you.
            </Typography>
          </Box>
          {editing ? (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button variant="muted" onClick={() => { setDraft(savedValues); setEditing(false); setBrandInput(''); setCategoryOtherInput(''); setGoalOtherInput(''); }}>Discard changes</Button>
              <Button onClick={save}>Save changes</Button>
            </Stack>
          ) : (
            <Button variant="secondary" onClick={() => { setDraft(savedValues); setEditing(true); }}>Edit</Button>
          )}
        </Stack>

        <Box sx={{ opacity: editing ? 1 : 0.58, transition: 'opacity 160ms ease' }}>
        <Divider sx={{ my: 2.5 }} />
        <Stack spacing={1.5}>
          <Box><Typography fontWeight={700}>What opportunities are you open to?</Typography><Typography variant="body2" color="text.secondary">Select the types of collaborations you're interested in. These choices may appear on your public profile.</Typography></Box>
          <ToggleCards disabled={!editing} options={OPPORTUNITY_PREFERENCES} values={draft.opportunityPreferences} onChange={(values) => update('opportunityPreferences', values)} />
        </Stack>

        <Divider sx={{ my: 3 }} />
        <Stack spacing={1.5}>
          <Box><Typography fontWeight={700}>Who would you love to work with?</Typography><Typography variant="body2" color="text.secondary">Tell us the brands or types of brands you'd be excited to collaborate with. This stays private and helps CreatorCV understand your interests.</Typography></Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small" fullWidth label="Wishlist brand (e.g. Nike, Medicube)" placeholder="Add a brand" value={brandInput}
              inputProps={{ maxLength: 100 }}
              helperText={`${draft.collaborationWishlistBrands.length}/10 brands`}
              disabled={!editing}
              onChange={(event) => setBrandInput(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addBrand(); } }}
            />
            <Button variant="secondary" onClick={addBrand} disabled={!editing || !brandInput.trim() || draft.collaborationWishlistBrands.length >= 10} sx={{ alignSelf: { sm: 'flex-start' }, minHeight: 40 }}>Add</Button>
          </Stack>
          {draft.collaborationWishlistBrands.length > 0 && (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
              {draft.collaborationWishlistBrands.map((brand) => <Chip key={brand.toLowerCase()} label={brand} disabled={!editing} onDelete={editing ? () => update('collaborationWishlistBrands', draft.collaborationWishlistBrands.filter((value) => value !== brand)) : undefined} />)}
            </Stack>
          )}
          <Typography variant="body2" fontWeight={650} sx={{ pt: 0.5 }}>Brand categories you're interested in</Typography>
          <SelectableChips disabled={!editing} options={BRAND_CATEGORY_OPTIONS} values={draft.collaborationWishlistCategories} onChange={(values) => update('collaborationWishlistCategories', values)} />
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" fontWeight={650}>Custom category</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" fullWidth label="Add a custom category" placeholder="e.g. Wellness" value={categoryOtherInput} inputProps={{ maxLength: 100 }} disabled={!editing} onChange={(event) => setCategoryOtherInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addCustom('collaborationWishlistCategories', categoryOtherInput, setCategoryOtherInput); } }} />
            <Button variant="secondary" onClick={() => addCustom('collaborationWishlistCategories', categoryOtherInput, setCategoryOtherInput)} disabled={!editing || !categoryOtherInput.trim()} sx={{ alignSelf: { sm: 'flex-start' }, minHeight: 40 }}>Add</Button>
          </Stack>
          {customValues(draft.collaborationWishlistCategories).length > 0 && (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
              {customValues(draft.collaborationWishlistCategories).map((category) => <Chip key={category.key} label={category.label} color={editing ? 'primary' : 'default'} variant={editing ? 'filled' : 'outlined'} disabled={!editing} onDelete={editing ? () => update('collaborationWishlistCategories', draft.collaborationWishlistCategories.filter((value) => value !== category.key)) : undefined} sx={{ height: 'auto', minHeight: 40, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }} />)}
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />
        <Stack spacing={1.5}>
          <Box><Typography fontWeight={700}>What are you hoping to achieve with CreatorCV?</Typography><Typography variant="body2" color="text.secondary">Select all that apply. Your goals stay private.</Typography></Box>
          <SelectableChips disabled={!editing} options={CREATOR_GOAL_OPTIONS} values={draft.creatorGoals} onChange={(values) => update('creatorGoals', values)} />
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" fontWeight={650}>Custom goal</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" fullWidth label="Add a custom goal" placeholder="e.g. Build a creative business" value={goalOtherInput} inputProps={{ maxLength: 100 }} disabled={!editing} onChange={(event) => setGoalOtherInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addCustom('creatorGoals', goalOtherInput, setGoalOtherInput); } }} />
            <Button variant="secondary" onClick={() => addCustom('creatorGoals', goalOtherInput, setGoalOtherInput)} disabled={!editing || !goalOtherInput.trim()} sx={{ alignSelf: { sm: 'flex-start' }, minHeight: 40 }}>Add</Button>
          </Stack>
          {customValues(draft.creatorGoals).length > 0 && (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
              {customValues(draft.creatorGoals).map((goal) => <Chip key={goal.key} label={goal.label} color={editing ? 'primary' : 'default'} variant={editing ? 'filled' : 'outlined'} disabled={!editing} onDelete={editing ? () => update('creatorGoals', draft.creatorGoals.filter((value) => value !== goal.key)) : undefined} sx={{ height: 'auto', minHeight: 40, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }} />)}
            </Stack>
          )}
        </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default OpportunityProfileEditor;
